import { CaretUpDownIcon } from '@phosphor-icons/react';
import { TriangleAlert } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';

import { Button } from '@/components/ui/button';
import { GenericTable, TableItemStatus } from '@/modules/common/components';

import { Event } from '../../types';

interface EventsTableProps {
  data: Event[];
  onClick: (item: Event) => void;
}

const columns: ColumnDef<Event>[] = [
  {
    size: 300,
    accessorKey: 'name',
    header: ({ column }) => {
      return (
        <Button
          variant="transparent"
          className="p-0 uppercase text-sm font-medium gap-1"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Name
          <CaretUpDownIcon className="max-h-4" />
        </Button>
      );
    },
    cell: ({ row }) => row.getValue('name'),
  },
  {
    size: 200,
    accessorKey: 'status',
    header: ({ column }) => {
      return (
        <Button
          variant="transparent"
          className="p-0 uppercase text-sm font-medium gap-1"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Status
          <CaretUpDownIcon className="max-h-4" />
        </Button>
      );
    },
    cell: ({ row }) => <TableItemStatus status={row.getValue('status')} />,
  },
  {
    accessorKey: 'aiModel',
    header: ({ column }) => {
      return (
        <Button
          variant="transparent"
          className="p-0 uppercase text-sm font-medium gap-1"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          AI Model
          <CaretUpDownIcon className="max-h-4" />
        </Button>
      );
    },
    cell: ({ row }) => row.getValue('aiModel'),
  },
  {
    size: 200,
    accessorKey: 'startDate',
    header: ({ column }) => {
      return (
        <Button
          variant="transparent"
          className="p-0 uppercase text-sm font-medium gap-1"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Start date
          <CaretUpDownIcon className="max-h-4" />
        </Button>
      );
    },
    cell: ({ row }) =>
      format(new Date(row.getValue('startDate')), 'yyyy-MM-dd'),
  },
  {
    size: 300,
    accessorKey: 'validationErrors',
    header: ({ column }) => {
      return (
        <div className="flex items-center gap-1">
          <TriangleAlert size={16} />
          Missing Items
        </div>
      );
    },
    cell: ({ row }) => {
      const keys = Object.keys(row.getValue('validationErrors'));

      if (keys?.length == 0) return "-";

      const mapKeyLabel = {
        info: 'Info',
        aiModel: 'AI Model',
        video: 'Video',
        models: 'Models',
        styles: 'Styles',
        masks: 'Masks',
        printers: 'Printers',
        participants: 'Participants',
        inventory: 'Inventory',
      };

      return (
        <div className="text-error-700">
          {keys.map((key) => mapKeyLabel[key]).join(', ')}
        </div>
      );
    },
  },
];

export function EventsTable({ data, onClick }: EventsTableProps) {
  return (
    <GenericTable
      data={data}
      onClick={onClick}
      columns={columns}
      searchable
      searchColumns={['name']}
      searchPlaceholder="Search by event name"
      emptyDescription="No event added yet"
    />
  );
}
